import { Route, Router } from "@solidjs/router";
import { ErrorBoundary, lazy, Suspense, type Component } from "solid-js";
import { RouteLoader } from "./components/Common";

// Eager imports - lightweight pages needed immediately
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";

// Lazy imports - heavy pages only loaded when visited
const ScrollDrivenDemos = lazy(() => import("./pages/demos/ScrollDrivenDemos"));
const TypographicLayersDemo = lazy(
  () => import("./pages/demos/TypographicLayersDemo")
);
const CodeTunnelDemo = lazy(() => import("./pages/demos/CodeTunnelDemo"));
const TerminalBootDemo = lazy(() => import("./pages/demos/TerminalBootDemo"));
const DescentDemo = lazy(() => import("./pages/demos/DescentDemo"));
const ViewTransitionsDemos = lazy(
  () => import("./pages/demos/ViewTransitionsDemos")
);
const ViewTransitionsDemo = lazy(
  () => import("./pages/demos/ViewTransitionsDemo")
);

const lazyRoute = (LazyComponent: Component) => () => (
  <Suspense fallback={<RouteLoader />}>
    <LazyComponent />
  </Suspense>
);

const App: Component = () => {
  return (
    <ErrorBoundary
      fallback={(err, reset) => (
        <div class="text-whitesmoke flex min-h-screen items-center justify-center bg-black p-4 font-mono">
          <div class="w-full max-w-2xl space-y-6">
            <div class="space-y-4 text-center">
              <h1 class="text-4xl font-bold text-red-500">
                Oops! Something went wrong
              </h1>
              <p class="text-lg text-gray-400">
                An unexpected error occurred while running the application.
              </p>
            </div>

            <div class="space-y-2 rounded-lg border border-gray-700 bg-gray-900 p-6">
              <h2 class="text-sm font-semibold tracking-wide text-gray-400 uppercase">
                Error Details
              </h2>
              <pre class="overflow-x-auto text-sm break-words whitespace-pre-wrap text-red-400">
                {err.message}
              </pre>
            </div>

            <div class="flex justify-center">
              <button
                onClick={() => reset()}
                class="transform rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-3 font-semibold text-white transition-all duration-200 hover:scale-105 hover:from-purple-700 hover:to-blue-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black focus:outline-none"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}
    >
      <Router>
        <Route path="/" component={Home} />

        {/* Scroll-Driven Demos */}
        <Route
          path="/demos/scroll-driven"
          component={lazyRoute(ScrollDrivenDemos)}
        />
        <Route
          path="/demos/scroll-driven/typographic-layers"
          component={lazyRoute(TypographicLayersDemo)}
        />
        <Route
          path="/demos/scroll-driven/code-tunnel"
          component={lazyRoute(CodeTunnelDemo)}
        />
        <Route
          path="/demos/scroll-driven/terminal-boot"
          component={lazyRoute(TerminalBootDemo)}
        />
        <Route
          path="/demos/scroll-driven/descent"
          component={lazyRoute(DescentDemo)}
        />

        {/* View Transition Demos */}
        <Route
          path="/demos/view-transitions"
          component={lazyRoute(ViewTransitionsDemos)}
        />
        <Route
          path="/demos/view-transitions/gallery"
          component={lazyRoute(ViewTransitionsDemo)}
        />

        <Route path="*" component={NotFound} />
      </Router>
    </ErrorBoundary>
  );
};

export default App;
